import { useMutation, useQuery, useSubscription } from '@apollo/client';
import * as React from 'react';

import {
  MARK_MENTORSHIP_NOTIFICATIONS_READ,
  MARK_MENTORSHIP_NOTIFICATIONS_SEEN,
  MENTORSHIP_NOTIFICATION_ADDED,
  MY_MENTORSHIP_NOTIFICATIONS,
} from './graphql';
import { MentorshipNotificationsPage } from './types';

export type MentorshipNotificationFilters = {
  programId?: string;
  workspaceId?: string;
  eventType?: string;
  resourceType?: string;
  limit?: number;
  offset?: number;
  skip?: boolean;
};

export const useMentorshipNotifications = (
  filters: MentorshipNotificationFilters = {}
) => {
  const {
    skip = false,
    eventType,
    limit,
    offset,
    programId,
    resourceType,
    workspaceId,
  } = filters;
  const variables = React.useMemo(
    () => ({
      input: {
        limit: limit ?? 50,
        offset: offset ?? 0,
        ...(eventType ? { eventType } : {}),
        ...(programId ? { programId } : {}),
        ...(resourceType ? { resourceType } : {}),
        ...(workspaceId ? { workspaceId } : {}),
      },
    }),
    [eventType, limit, offset, programId, resourceType, workspaceId]
  );
  const query = useQuery<{
    myMentorshipNotifications: MentorshipNotificationsPage;
  }>(MY_MENTORSHIP_NOTIFICATIONS, {
    variables,
    fetchPolicy: 'network-only',
    skip,
  });
  const [markSeenMutation, markSeenState] = useMutation(
    MARK_MENTORSHIP_NOTIFICATIONS_SEEN
  );
  const [markReadMutation, markReadState] = useMutation(
    MARK_MENTORSHIP_NOTIFICATIONS_READ
  );

  useSubscription(MENTORSHIP_NOTIFICATION_ADDED, {
    skip,
    onSubscriptionData: () => {
      void query.refetch();
    },
  });

  const markSeen = React.useCallback(
    async (eventIds?: string[]) => {
      await markSeenMutation({
        variables: { eventIds: eventIds?.length ? eventIds : null },
      });
      await query.refetch();
    },
    [markSeenMutation, query]
  );

  const markRead = React.useCallback(
    async (eventIds: string[]) => {
      if (!eventIds.length) return;
      await markReadMutation({ variables: { eventIds } });
      await query.refetch();
    },
    [markReadMutation, query]
  );

  return {
    ...query,
    notifications: query.data?.myMentorshipNotifications,
    markSeen,
    markRead,
    markingSeen: markSeenState.loading,
    markingRead: markReadState.loading,
  };
};
